import { useMemo, useState } from "react";
import ConnectionCard from "../components/ConnectionCard";
import ConnectWizard, { type ReconnectTarget } from "../components/ConnectWizard";
import CopyToTeamDialog from "../components/CopyToTeamDialog";
import {
  enablementFor,
  scopeOf,
  slugOf,
  type CatalogProvider,
  type Connection,
} from "../types";
import { useDashboard } from "./Dashboard";

/**
 * How many accounts it takes before the field is worth its own row.
 *
 * Under this many, the grid *is* the answer: every card is on screen at once and reading
 * three headings beats typing. Past it the page becomes a list you scan rather than one
 * you see, which is the moment a filter starts saving work instead of taking up room --
 * the same reason the catalog is not in the grid.
 */
const SEARCH_FROM = 4;

/**
 * What the server exposes: the accounts connected to it, and the switch on each one.
 * Nothing here can change the endpoint or the token -- that is the whole point of the
 * split. Connecting, renaming and switching off are all safe from this page, because none
 * of them reach the MCP client's config.
 *
 * The grid is the *live* thing only: one card per connected account, and nothing else. The
 * catalog used to sit in it as a row of never-connected services, which made a page that
 * is meant to answer "what is my server serving right now?" mostly a list of things it is
 * not. The catalog is a question you ask once per account -- so it lives behind Add
 * provider, in the wizard, where it is the first step rather than permanent furniture.
 *
 * The filter over the grid is a *name* filter and deliberately only that. The question it
 * answers is "which card is the one I mean?" -- so it matches what the card actually says:
 * the account's label, its service, and its tool-name prefix. "Which account can read my
 * calendar?" is the other question, it has no account in it, and it already has a page:
 * /tools searches every tool across every account, in plain language. Answering it here
 * too would be the same search on a surface organised by the wrong thing.
 */
export default function Providers() {
  const {
    catalog,
    connections,
    server,
    enabled,
    toggleConnection,
    notify,
    project,
    projects,
    teams,
    user,
  } = useDashboard();

  const team = teams.find((t) => t.id === project?.teamId) ?? null;
  /** A private project (personal team) has no one to share with; no Team category. */
  const teamProject = Boolean(team && !team.personal);
  const teamName = team?.name ?? "your team";

  /** What the user has typed into the filter over the grid. */
  const [query, setQuery] = useState("");

  /** Which flow the wizard is on, if it is open at all. */
  const [wizard, setWizard] = useState<{ reconnect: ReconnectTarget | null } | null>(null);

  /** The account whose copy-to-team dialog is open, if any. */
  const [copying, setCopying] = useState<{
    conn: Connection;
    provider: CatalogProvider;
  } | null>(null);

  /**
   * Where a copy could go: real teams only. The personal team has no one to share
   * with, and the backend refuses it -- so with no real team, the action is not
   * offered at all rather than offered and then explained away.
   */
  const copyTeams = useMemo(() => teams.filter((t) => !t.personal), [teams]);

  const providerOf = useMemo(
    () => new Map(catalog.map((p) => [p.id, p])),
    [catalog]
  );

  /**
   * One card per connected account, grouped by service so two GitHub accounts sit
   * together, and stably sorted so a card does not reshuffle itself on every document
   * update. An account is not a detail of a service -- it has its own credential, switch
   * and tool names -- so it gets a card of its own rather than a row inside one.
   */
  const accounts = useMemo(() => {
    const paired = connections.flatMap((conn) => {
      const provider = providerOf.get(conn.providerId);
      // A connection whose provider is switched off for this deployment has nothing we
      // can render -- no icon, no tool list. It stays in Firestore, untouched.
      return provider ? [{ conn, provider }] : [];
    });

    return paired.sort(
      (a, b) =>
        a.provider.name.localeCompare(b.provider.name) ||
        slugOf(a.conn).localeCompare(slugOf(b.conn))
    );
  }, [connections, providerOf]);

  const searchable = accounts.length >= SEARCH_FROM;

  /**
   * The filter, or nothing at all when the field is not on screen. Read through this and
   * never through `query`, so that disconnecting down to two accounts cannot leave a
   * filter running with no field left to clear it.
   */
  const needle = searchable ? query.trim().toLowerCase() : "";

  /**
   * The cards the filter leaves, matched on what the card itself shows: the account's own
   * label, the service it is, and the prefix its tools carry into the MCP client. Nothing
   * invisible -- a card that matches has the word on it, which is what makes an instant
   * substring filter honest about why it kept a row.
   */
  const matches = useMemo(() => {
    if (!needle) return accounts;
    return accounts.filter(({ conn, provider }) =>
      `${conn.accountLabel} ${provider.name} ${slugOf(conn)}`
        .toLowerCase()
        .includes(needle)
    );
  }, [accounts, needle]);

  const shared = matches.filter(({ conn }) => scopeOf(conn) === "team");
  const personal = matches.filter(({ conn }) => scopeOf(conn) === "personal");

  function cardsFor(list: { conn: Connection; provider: CatalogProvider }[]) {
    return (
      <div className="grid">
        {list.map(({ conn, provider }) => (
          <ConnectionCard
            key={conn.id}
            provider={provider}
            conn={conn}
            uid={user.uid}
            teamName={teamName}
            enabled={enabled.has(slugOf(conn))}
            allowList={enablementFor(conn, server).allowList}
            onToggle={toggleConnection}
            onReconnect={() => setWizard({ reconnect: { provider, conn } })}
            onCopy={
              // Only the account's owner can share it -- the copy carries their
              // credential -- and only when there is a team to share with.
              conn.ownerUid === user.uid && copyTeams.length > 0
                ? () => setCopying({ conn, provider })
                : null
            }
            canShare={
              // Moving to team level only means something in a team project, and a
              // personal connection in the caller's view is always their own.
              teamProject && scopeOf(conn) === "personal"
            }
            notify={notify}
          />
        ))}
      </div>
    );
  }

  return (
    <section className="section">
      <div className="section-head">
        <div>
          <h2>Providers</h2>
          <p className="section-sub">
            Every connected account is its own card with its own tools — connect the same
            service twice and you get two, independent of each other. Switching one off
            hides its tools immediately; your MCP URL never changes.
          </p>
        </div>

        {accounts.length > 0 && (
          <button className="btn" onClick={() => setWizard({ reconnect: null })}>
            Add provider
          </button>
        )}
      </div>

      {/* A field and nothing beside it: unlike /tools there is no second, slower search
          to offer -- an account is found by the name it already has, as you type. */}
      {searchable && (
        <div className="conn-search">
          <input
            className="search-input"
            value={query}
            placeholder="Filter accounts by name, service or prefix…"
            aria-label="Filter accounts"
            spellCheck={false}
            autoComplete="off"
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      )}

      {needle && matches.length > 0 && (
        <p className="conn-search-count">
          {matches.length} of {accounts.length} accounts match “{query.trim()}”
          {" · "}
          <button className="link-btn" onClick={() => setQuery("")}>
            show all {accounts.length}
          </button>
        </p>
      )}

      {accounts.length === 0 ? (
        <div className="empty">
          <p>No accounts connected yet.</p>
          <p className="empty-sub">
            Add a provider and its tools appear in the MCP client you already installed —
            no config change, no restart.
          </p>
          <button
            className="btn empty-cta"
            onClick={() => setWizard({ reconnect: null })}
          >
            Add provider
          </button>
        </div>
      ) : matches.length === 0 ? (
        // Nothing matched. The filter reads only what is written on a card, so the useful
        // thing to say is what the *other* search can do: the account whose tools do the
        // job is a question about tools, and /tools answers it in plain language.
        <div className="empty">
          <p>No account matches “{query.trim()}”.</p>
          <p className="empty-sub">
            This searches account names, services and tool prefixes. Looking for what an
            account can <em>do</em>? The Tools page searches every tool across all of them.
          </p>
          <button className="btn empty-cta" onClick={() => setQuery("")}>
            Show all {accounts.length}
          </button>
        </div>
      ) : teamProject ? (
        // In a team project the who-can-use-it split is the first thing to know about an
        // account, so the page is organised by it: what the whole team shares, then what
        // is yours alone. A private project has no such split -- everything is private.
        <>
          {/* Under a filter a half with no matches is dropped rather than explained: its
              prose ("nothing is shared yet") would be a claim about the project, and what
              is true is only that nothing there matched. The surviving heading still says
              which side the match came from, which is the point of the split. */}
          {(shared.length > 0 || !needle) && (
            <div className="conn-group">
              <header className="conn-group-head">
                <h3>Team</h3>
                <p>
                  Shared with everyone in {teamName} — every member's server for this
                  project gets these tools.
                </p>
              </header>
              {shared.length > 0 ? (
                cardsFor(shared)
              ) : (
                <p className="conn-group-empty">
                  Nothing is shared yet. Add a provider and choose
                  “Everyone in {teamName}”, or share one of your private accounts below.
                </p>
              )}
            </div>
          )}

          {(personal.length > 0 || !needle) && (
            <div className="conn-group">
              <header className="conn-group-head">
                <h3>Private</h3>
                <p>Only you — these accounts never appear in a teammate's client.</p>
              </header>
              {personal.length > 0 ? (
                cardsFor(personal)
              ) : (
                <p className="conn-group-empty">
                  No private accounts in this project yet. Add a provider and choose “Just
                  me”.
                </p>
              )}
            </div>
          )}
        </>
      ) : (
        cardsFor(matches)
      )}

      {wizard && project && (
        <ConnectWizard
          catalog={catalog}
          connections={connections}
          projectId={project.id}
          teamName={teamProject ? teamName : null}
          reconnect={wizard.reconnect}
          onClose={() => setWizard(null)}
        />
      )}

      {copying && (
        <CopyToTeamDialog
          provider={copying.provider}
          conn={copying.conn}
          teams={copyTeams}
          projects={projects}
          onClose={() => setCopying(null)}
          notify={notify}
        />
      )}
    </section>
  );
}
